/**
 * AeDaily.tsx
 *
 * W37-B: AE Today Command Center — Mi6 Attilius Intelligence Platform
 * Route: /#/today (role-routed for AE)
 *
 * 6-section command center — all data sourced live from Supabase Tenant 1.
 * Replaces the empty news shell with real, rep-scoped intelligence.
 *
 * Sections:
 *   1. Top accounts to advance (account_plans + accounts, composite_score DESC)
 *   2. Open actions (cadence_enrollments, completed_at IS NULL, due_date ASC)
 *   3. Pipeline coverage vs quota (v_ae_pipeline_summary + rep_quota_plan)
 *   4. Daily plays (cadence_enrollments with marketing_hook_kind, due today)
 *   5. Coaching hooks (coaching_notes, rep_visible, created_at DESC)
 *   6. Score movement (progression_snapshot week-over-week delta)
 *
 * Canon authority:
 *   - 5-color palette only: gray/red/orange/yellow/green. No emerald, teal, blue, indigo, purple, pink, amber.
 *   - No emojis. No exclamation points. M-PACT hyphenated.
 *   - "Checkpoint" not "Gate". CP format: "CP{N} — {Name}".
 *   - HARD-KILL / BANT / MEDDIC banned on user-facing surfaces.
 *   - Empty states always render — never a blank section.
 *   - Tenant 1 only. No phantom defaults.
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { getSession, api } from "@/lib/session";
import { LoadingStrip } from "@/pages/dashboards/live-data";

// ── Brand constants ───────────────────────────────────────────────────────────
const BG = "#1C1F26";
const CARD = "#252930";
const BORDER = "#2D3139";
const MI6_RED = "#DC2626";
const TEXT_PRIMARY = "#FFFFFF";
const TEXT_SECONDARY = "#9CA3AF";
const TEXT_MUTED = "#6B7280";

// ── 5-band M-PACT scoring palette ────────────────────────────────────────────
function mpactColor(score: number): string {
  if (score >= 80) return "#16A34A";
  if (score >= 65) return "#16A34A";
  if (score >= 50) return "#EAB308";
  if (score >= 30) return "#EA580C";
  return "#DC2626";
}

// ── Utility helpers ───────────────────────────────────────────────────────────
function fmtMoney(n: number): string {
  if (!isFinite(n) || n === 0) return "$0";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function dateLabel(): string {
  return new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function daysRemainingInCurrentQuarter(): number {
  const now = new Date();
  const month = now.getMonth();
  const quarterEndMonth = Math.ceil((month + 1) / 3) * 3 - 1;
  const quarterEnd = new Date(now.getFullYear(), quarterEndMonth + 1, 0);
  return Math.max(0, Math.ceil((quarterEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}

function currentQuarterLabel(): string {
  const month = new Date().getMonth();
  const q = Math.ceil((month + 1) / 3);
  return `Q${q}`;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface TopAccount {
  account_id: number;
  account_name: string;
  composite_score: number;
  cp_number: number;
  cp_label: string;
  account_status: string;
  score_delta_7d: number | null;
  plan_status: string;
  updated_at: string | null;
}

interface OpenAction {
  id: string;
  prospect_name: string;
  account_name: string;
  due_date: string | null;
  action_kind: string;
  notes: string | null;
  is_overdue: boolean;
  is_today: boolean;
  status: string;
}

interface DailyPlay {
  id: string;
  prospect_name: string;
  account_name: string;
  hook_kind: string;
  due_date: string;
  notes: string | null;
}

interface PipelineSummary {
  has_quota: boolean;
  annual_quota: number;
  quarter_quota: number;
  closed_ytd: number;
  weighted_pipeline: number;
  coverage_ratio: number;
  gap: number;
  pace_pct: number;
  days_remaining: number;
}

interface CoachingNote {
  id: string;
  body: string;
  visibility: string;
  created_at: string;
  manager_user_id: string;
}

interface ScoreMovement {
  account_id: number;
  account_name: string;
  account_status: string;
  current_score: number;
  prev_score: number | null;
  delta_7d: number | null;
  snapshot_date: string;
  prev_snapshot_date: string | null;
}

// ── Section 1: Top Accounts to Advance ───────────────────────────────────────

function SectionTopAccounts() {
  const [accounts, setAccounts] = useState<TopAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  useEffect(() => {
    api<{ accounts: TopAccount[] }>("/api/today/ae/top-accounts")
      .then((res) => setAccounts(res.accounts ?? []))
      .catch((e) => setError(e?.message ?? "Could not load accounts"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      className="rounded border p-5"
      style={{ background: CARD, borderColor: BORDER }}
    >
      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: TEXT_SECONDARY }}>
          Section 1
        </p>
        <p className="font-semibold" style={{ color: TEXT_PRIMARY }}>
          Top Accounts to Advance
        </p>
        <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
          Your highest composite scoring accounts — ranked by composite score, live data.
        </p>
      </div>

      {loading && <LoadingStrip height={80} />}
      {!loading && error && (
        <p className="text-sm" style={{ color: MI6_RED }}>{error}</p>
      )}
      {!loading && !error && accounts.length === 0 && (
        <p className="text-sm" style={{ color: TEXT_MUTED }}>
          No accounts to advance today.
        </p>
      )}
      {!loading && !error && accounts.length > 0 && (
        <ul className="space-y-3">
          {accounts.map((acct) => (
            <li
              key={acct.account_id}
              className="border-t pt-3 first:border-0 first:pt-0"
              style={{ borderColor: BORDER }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      className="text-sm font-medium hover:underline text-left"
                      style={{ color: TEXT_PRIMARY }}
                      onClick={() => setLocation(`/account/${acct.account_id}`)}
                    >
                      {acct.account_name}
                    </button>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{ background: BG, color: TEXT_SECONDARY }}
                    >
                      {acct.account_status}
                    </span>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded font-semibold"
                      style={{ background: BG, color: mpactColor(acct.composite_score) }}
                    >
                      Composite {acct.composite_score.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: TEXT_MUTED }}>
                    {acct.cp_label}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  {acct.score_delta_7d !== null ? (
                    <span
                      className="text-xs font-semibold"
                      style={{
                        color:
                          acct.score_delta_7d > 0
                            ? "#16A34A"
                            : acct.score_delta_7d < 0
                            ? MI6_RED
                            : TEXT_MUTED,
                      }}
                    >
                      {acct.score_delta_7d > 0 ? "+" : ""}
                      {acct.score_delta_7d.toFixed(1)} pts (7d)
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: TEXT_MUTED }}>
                      No prior snapshot
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Section 2: Open Actions ───────────────────────────────────────────────────

function SectionOpenActions() {
  const [actions, setActions] = useState<OpenAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ open_actions: OpenAction[] }>("/api/today/ae/open-actions")
      .then((res) => setActions(res.open_actions ?? []))
      .catch((e) => setError(e?.message ?? "Could not load open actions"))
      .finally(() => setLoading(false));
  }, []);

  function actionKindLabel(kind: string): string {
    if (!kind) return "Touch";
    const k = kind.toLowerCase();
    if (k.includes("call") || k.includes("phone")) return "Call";
    if (k.includes("email")) return "Email";
    if (k.includes("linkedin") || k.includes("social")) return "LinkedIn";
    if (k.includes("meeting") || k.includes("demo")) return "Meeting";
    return kind.charAt(0).toUpperCase() + kind.slice(1);
  }

  return (
    <section
      className="rounded border p-5"
      style={{ background: CARD, borderColor: BORDER }}
    >
      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: TEXT_SECONDARY }}>
          Section 2
        </p>
        <p className="font-semibold" style={{ color: TEXT_PRIMARY }}>
          Open Actions
        </p>
        <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
          Your cadence actions not yet completed — sorted by due date.
        </p>
      </div>

      {loading && <LoadingStrip height={80} />}
      {!loading && error && (
        <p className="text-sm" style={{ color: MI6_RED }}>{error}</p>
      )}
      {!loading && !error && actions.length === 0 && (
        <p className="text-sm" style={{ color: TEXT_MUTED }}>
          No open actions.
        </p>
      )}
      {!loading && !error && actions.length > 0 && (
        <ul className="space-y-3">
          {actions.map((action) => (
            <li
              key={action.id}
              className="border-t pt-3 first:border-0 first:pt-0"
              style={{ borderColor: BORDER }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium" style={{ color: TEXT_PRIMARY }}>
                      {action.prospect_name}
                    </span>
                    {action.account_name && (
                      <span className="text-xs" style={{ color: TEXT_SECONDARY }}>
                        {action.account_name}
                      </span>
                    )}
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{ background: BG, color: TEXT_SECONDARY }}
                    >
                      {actionKindLabel(action.action_kind)}
                    </span>
                    {action.is_overdue && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded font-semibold"
                        style={{ background: "#7F1D1D", color: "#FCA5A5" }}
                      >
                        Overdue
                      </span>
                    )}
                    {action.is_today && !action.is_overdue && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded font-semibold"
                        style={{ background: "#1C3A2A", color: "#16A34A" }}
                      >
                        Due today
                      </span>
                    )}
                  </div>
                  {action.notes && (
                    <p className="text-xs mt-1 truncate" style={{ color: TEXT_MUTED }}>
                      {action.notes}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className="text-xs" style={{ color: TEXT_MUTED }}>
                    {action.due_date ? fmtDate(action.due_date) : "No due date"}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Section 3: Pipeline Coverage vs Quota ────────────────────────────────────

function SectionPipelineCoverage() {
  const [pipeline, setPipeline] = useState<PipelineSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<PipelineSummary>("/api/today/gap-math")
      .then((res) => setPipeline(res))
      .catch((e) => setError(e?.message ?? "Could not load pipeline data"))
      .finally(() => setLoading(false));
  }, []);

  function coverageBarColor(ratio: number): string {
    if (ratio >= 5) return "#16A34A";
    if (ratio >= 3.5) return "#16A34A";
    if (ratio >= 2) return "#EA580C";
    return MI6_RED;
  }

  const daysLeft = daysRemainingInCurrentQuarter();

  return (
    <section
      className="rounded border p-5"
      style={{ background: CARD, borderColor: BORDER }}
    >
      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: TEXT_SECONDARY }}>
          Section 3
        </p>
        <p className="font-semibold" style={{ color: TEXT_PRIMARY }}>
          Pipeline Coverage vs Quota
        </p>
        <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
          Weighted pipeline against your remaining quota gap. 5x coverage is the suggested threshold.
        </p>
      </div>

      {loading && <LoadingStrip height={80} />}
      {!loading && error && (
        <p className="text-sm" style={{ color: MI6_RED }}>{error}</p>
      )}
      {!loading && !error && !pipeline && (
        <p className="text-sm" style={{ color: TEXT_MUTED }}>
          No pipeline data available.
        </p>
      )}
      {!loading && !error && pipeline && (
        <div className="space-y-4">
          {!pipeline.has_quota && (
            <div
              className="text-xs px-3 py-2 rounded border"
              style={{ background: BG, borderColor: "#92400E", color: "#FCD34D" }}
            >
              Quota not yet set. Coverage ratio shown against 3x weighted pipeline as a placeholder.
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: TEXT_SECONDARY }}>
                {pipeline.has_quota ? "Annual Quota" : "Quota"}
              </p>
              <p className="text-xl font-bold" style={{ color: TEXT_PRIMARY }}>
                {pipeline.has_quota ? fmtMoney(pipeline.annual_quota) : "Not set"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: TEXT_SECONDARY }}>
                {currentQuarterLabel()} Closed
              </p>
              <p className="text-xl font-bold" style={{ color: TEXT_PRIMARY }}>
                {fmtMoney(pipeline.closed_ytd)}
              </p>
              {pipeline.has_quota && pipeline.quarter_quota > 0 && (
                <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                  {Math.round(pipeline.pace_pct)}% of {currentQuarterLabel()} target ({fmtMoney(pipeline.quarter_quota)})
                </p>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: TEXT_SECONDARY }}>
                Weighted Pipeline
              </p>
              <p className="text-xl font-bold" style={{ color: TEXT_PRIMARY }}>
                {fmtMoney(pipeline.weighted_pipeline)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: TEXT_SECONDARY }}>
                Coverage Ratio
              </p>
              <p
                className="text-xl font-bold"
                style={{ color: coverageBarColor(pipeline.coverage_ratio) }}
              >
                {pipeline.coverage_ratio.toFixed(1)}x
              </p>
              <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                5x suggested — gap is {fmtMoney(pipeline.gap)}
              </p>
            </div>
          </div>
          {/* Coverage bar */}
          <div>
            <div className="w-full rounded-full h-2 mb-1" style={{ background: BORDER }}>
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  width: `${Math.min(100, (pipeline.coverage_ratio / 5) * 100)}%`,
                  background: coverageBarColor(pipeline.coverage_ratio),
                }}
              />
            </div>
            <p className="text-xs" style={{ color: TEXT_MUTED }}>
              {daysLeft} days remaining in {currentQuarterLabel()}.
              {pipeline.has_quota
                ? " Suggested pipeline for 5x coverage: " + fmtMoney(pipeline.gap * 5) + "."
                : " Quota not yet set — work with your manager to confirm the target."}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Section 4: Daily Plays ────────────────────────────────────────────────────

function SectionDailyPlays() {
  const [plays, setPlays] = useState<DailyPlay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ daily_plays: DailyPlay[] }>("/api/today/ae/open-actions")
      .then((res) => setPlays(res.daily_plays ?? []))
      .catch((e) => setError(e?.message ?? "Could not load daily plays"))
      .finally(() => setLoading(false));
  }, []);

  function hookKindLabel(kind: string): string {
    if (!kind) return "Marketing play";
    const k = kind.toLowerCase();
    if (k.includes("event")) return "Event follow-up";
    if (k.includes("content") || k.includes("whitepaper")) return "Content share";
    if (k.includes("webinar")) return "Webinar follow-up";
    if (k.includes("email")) return "Email outreach";
    if (k.includes("call")) return "Call play";
    return kind.charAt(0).toUpperCase() + kind.slice(1).replace(/_/g, " ");
  }

  return (
    <section
      className="rounded border p-5"
      style={{ background: CARD, borderColor: BORDER }}
    >
      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: TEXT_SECONDARY }}>
          Section 4
        </p>
        <p className="font-semibold" style={{ color: TEXT_PRIMARY }}>
          Daily Plays
        </p>
        <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
          Marketing-sourced plays pulled into your cadence for today.
        </p>
      </div>

      {loading && <LoadingStrip height={60} />}
      {!loading && error && (
        <p className="text-sm" style={{ color: MI6_RED }}>{error}</p>
      )}
      {!loading && !error && plays.length === 0 && (
        <p className="text-sm" style={{ color: TEXT_MUTED }}>
          No daily plays scheduled for today.
        </p>
      )}
      {!loading && !error && plays.length > 0 && (
        <ul className="space-y-3">
          {plays.map((play) => (
            <li
              key={play.id}
              className="border-t pt-3 first:border-0 first:pt-0"
              style={{ borderColor: BORDER }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium" style={{ color: TEXT_PRIMARY }}>
                      {play.prospect_name}
                    </span>
                    {play.account_name && (
                      <span className="text-xs" style={{ color: TEXT_SECONDARY }}>
                        {play.account_name}
                      </span>
                    )}
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{ background: "#1C2E1C", color: "#16A34A", border: "1px solid #2D5A2D" }}
                    >
                      {hookKindLabel(play.hook_kind)}
                    </span>
                  </div>
                  {play.notes && (
                    <p className="text-xs mt-1" style={{ color: TEXT_MUTED }}>
                      {play.notes}
                    </p>
                  )}
                </div>
                <span className="text-xs flex-shrink-0" style={{ color: TEXT_MUTED }}>
                  Today
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Section 5: Coaching Hooks ─────────────────────────────────────────────────

function SectionCoachingHooks() {
  const [notes, setNotes] = useState<CoachingNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    api<{ notes: CoachingNote[] }>("/api/coaching/notes")
      .then((res) => setNotes((res.notes ?? []).slice(0, 5)))
      .catch((e) => setError(e?.message ?? "Could not load coaching notes"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      className="rounded border p-5"
      style={{ background: CARD, borderColor: BORDER }}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: TEXT_SECONDARY }}>
            Section 5
          </p>
          <p className="font-semibold" style={{ color: TEXT_PRIMARY }}>
            Coaching Hooks
          </p>
          <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
            Notes from your last 1:1 — left by your manager (rep-visible only).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs flex-shrink-0"
          style={{ color: TEXT_MUTED }}
        >
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>

      {loading && <LoadingStrip height={60} />}
      {!loading && error && (
        <p className="text-sm" style={{ color: MI6_RED }}>{error}</p>
      )}
      {!loading && !error && expanded && notes.length === 0 && (
        <p className="text-sm" style={{ color: TEXT_MUTED }}>
          No coaching notes yet.
        </p>
      )}
      {!loading && !error && expanded && notes.length > 0 && (
        <ul className="space-y-3">
          {notes.map((note) => (
            <li
              key={note.id}
              className="border-t pt-3 first:border-0 first:pt-0"
              style={{ borderColor: BORDER }}
            >
              <p className="text-sm leading-relaxed" style={{ color: TEXT_PRIMARY }}>
                {note.body}
              </p>
              <p className="text-xs mt-1.5" style={{ color: TEXT_MUTED }}>
                {fmtDate(note.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Section 6: Score Movement ─────────────────────────────────────────────────

function SectionScoreMovement() {
  const [movements, setMovements] = useState<ScoreMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  useEffect(() => {
    api<{ movements: ScoreMovement[] }>("/api/today/ae/score-movement")
      .then((res) => setMovements(res.movements ?? []))
      .catch((e) => setError(e?.message ?? "Could not load score movement"))
      .finally(() => setLoading(false));
  }, []);

  function deltaDisplay(delta: number | null): { label: string; color: string } {
    if (delta === null) return { label: "No prior data", color: TEXT_MUTED };
    if (delta === 0) return { label: "No change", color: TEXT_MUTED };
    if (delta > 0) return { label: `+${delta.toFixed(1)} pts`, color: "#16A34A" };
    return { label: `${delta.toFixed(1)} pts`, color: MI6_RED };
  }

  return (
    <section
      className="rounded border p-5"
      style={{ background: CARD, borderColor: BORDER }}
    >
      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: TEXT_SECONDARY }}>
          Section 6
        </p>
        <p className="font-semibold" style={{ color: TEXT_PRIMARY }}>
          Score Movement
        </p>
        <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
          Week-over-week Composite score delta across your accounts — live from progression snapshots.
        </p>
      </div>

      {loading && <LoadingStrip height={80} />}
      {!loading && error && (
        <p className="text-sm" style={{ color: MI6_RED }}>{error}</p>
      )}
      {!loading && !error && movements.length === 0 && (
        <p className="text-sm" style={{ color: TEXT_MUTED }}>
          No score movement data available yet. Snapshots are recorded daily.
        </p>
      )}
      {!loading && !error && movements.length > 0 && (
        <ul className="space-y-3">
          {movements.map((mv) => {
            const { label, color } = deltaDisplay(mv.delta_7d);
            return (
              <li
                key={mv.account_id}
                className="border-t pt-3 first:border-0 first:pt-0"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      className="text-sm font-medium hover:underline text-left"
                      style={{ color: TEXT_PRIMARY }}
                      onClick={() => setLocation(`/account/${mv.account_id}`)}
                    >
                      {mv.account_name}
                    </button>
                    <span className="text-xs" style={{ color: TEXT_SECONDARY }}>
                      {mv.account_status}
                    </span>
                    <span
                      className="text-xs font-semibold px-1.5 py-0.5 rounded"
                      style={{ background: BG, color: mpactColor(mv.current_score) }}
                    >
                      Composite {mv.current_score.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-xs font-semibold flex-shrink-0" style={{ color }}>
                    {label}
                  </span>
                </div>
                {mv.prev_snapshot_date && (
                  <p className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                    vs. {fmtDate(mv.prev_snapshot_date)}
                    {mv.prev_score !== null && ` (was ${mv.prev_score.toFixed(1)})`}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ── Page Header ───────────────────────────────────────────────────────────────

function PageHeader({ name }: { name: string }) {
  return (
    <header
      className="w-full border-b px-6 py-4 flex items-center justify-between"
      style={{ background: BG, borderColor: BORDER }}
    >
      <div className="flex items-center gap-3">
        <span className="text-lg font-semibold" style={{ color: TEXT_PRIMARY }}>
          {name}
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded uppercase tracking-wide"
          style={{ background: BORDER, color: TEXT_SECONDARY }}
        >
          AE
        </span>
        <span className="text-xs" style={{ color: TEXT_MUTED }}>
          Today Command Center
        </span>
      </div>
      <div className="text-sm" style={{ color: TEXT_SECONDARY }}>
        {dateLabel()}
      </div>
    </header>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function AeDaily() {
  const session = getSession();
  const repName = session?.name ?? "AE";

  return (
    <div className="min-h-screen text-white" style={{ background: BG }}>
      {/* Page header */}
      <PageHeader name={repName} />

      {/* 6-section grid */}
      <div className="px-6 py-6 space-y-6 max-w-5xl mx-auto">

        {/* Section 1: Top Accounts to Advance */}
        <SectionTopAccounts />

        {/* Section 2: Open Actions */}
        <SectionOpenActions />

        {/* Section 3 + 4 side by side */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SectionPipelineCoverage />
          <SectionDailyPlays />
        </div>

        {/* Section 5: Coaching Hooks */}
        <SectionCoachingHooks />

        {/* Section 6: Score Movement */}
        <SectionScoreMovement />
      </div>

      {/* Brand footer */}
      <footer
        className="w-full py-4 text-center text-xs border-t"
        style={{ color: TEXT_MUTED, borderColor: BORDER }}
      >
        Attilius Support · Mi6 Group
      </footer>
    </div>
  );
}
